import { Link } from "react-router-dom";
import { ArrowUpRight, MessageCircle, Phone } from "lucide-react";
import SEO from "../seo/SEO";
import {
  physicianSchema,
  medicalClinicSchemas,
  breadcrumbSchema,
  webPageSchema,
} from "../seo/schemas";
import { PHONE_TEL, WHATSAPP_URL } from "../data/site";

/**
 * Generic editorial listing page used by /symptoms /diseases /procedures.
 *
 * Props:
 *   kind: "Symptom" | "Condition" | "Procedure"
 *   slug: "symptoms" | "diseases" | "procedures"
 *   title, subtitle, eyebrow
 *   items: [{ slug, shortTitle, blurb }]
 *   seoTitle, seoDescription
 */
export default function ListingPage({
  kind,
  slug,
  title,
  subtitle,
  eyebrow,
  items = [],
  seoTitle,
  seoDescription,
}) {
  return (
    <main data-testid={`${slug}-listing-page`}>
      <SEO
        title={seoTitle}
        description={seoDescription}
        canonical={`/${slug}`}
        schemas={[
          physicianSchema(),
          ...medicalClinicSchemas(),
          webPageSchema({ name: title, description: seoDescription, url: `/${slug}` }),
          breadcrumbSchema([
            { label: "Home", url: "/" },
            { label: kind === "Condition" ? "Conditions" : kind + "s" },
          ]),
        ]}
      />

      <section className="border-b border-black/10">
        <div className="container-x py-14 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8">
            <div className="overline">{eyebrow}</div>
            <h1
              data-testid="listing-h1"
              className="font-serif-display mt-5 text-5xl sm:text-6xl lg:text-[72px] leading-[1.02] tracking-tight"
            >
              {title}
            </h1>
            <p className="mt-7 text-black/70 text-[17px] leading-relaxed max-w-2xl">
              {subtitle}
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="btn-primary"
                data-testid="listing-book-btn"
              >
                <MessageCircle className="h-4 w-4" /> Book Appointment
              </a>
              <a href={PHONE_TEL} className="btn-ghost" data-testid="listing-call-btn">
                <Phone className="h-4 w-4" /> Call Now
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-x">
          <h2 className="font-serif-display text-3xl md:text-4xl tracking-tight mb-10">
            All {kind === "Condition" ? "conditions" : kind.toLowerCase() + "s"} we treat in Varanasi.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10 border border-black/10 rounded-[6px] overflow-hidden">
            {items.map((it, i) => (
              <Link
                key={it.slug}
                to={`/${slug}/${it.slug}`}
                data-testid={`listing-item-${it.slug}`}
                className="group bg-white p-7 md:p-8 flex flex-col gap-4 min-h-[200px] hover:bg-[#EAF6FD] transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] tracking-[0.22em] uppercase text-[#0370AD] font-semibold">
                    {kind} · {String(i + 1).padStart(2, "0")}
                  </span>
                  <ArrowUpRight className="h-4 w-4 arrow text-black/40 group-hover:text-[#0370AD]" />
                </div>
                <div className="mt-auto">
                  <h3 className="font-display-semibold text-2xl md:text-[26px] leading-tight">
                    {it.shortTitle}
                  </h3>
                  {it.blurb && (
                    <p className="mt-3 text-[13.5px] text-black/65 leading-relaxed">
                      {it.blurb}
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
